import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Stack, Typography, IconButton, Chip, Avatar, Button,
  TextField, useTheme, alpha, Skeleton, Tooltip, CircularProgress, Divider,
  Tabs, Tab
} from '@mui/material';
import {
  Close, MoreVert, Business, Edit, Save, Cancel, VpnKey, PersonAdd,
  EventAvailable
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { schoolService, School, SchoolAdmin } from '../../services/schoolService';
import { toUploadUrl } from '../../utils/uploadUrl';
import ConfirmationDialog from '../common/ConfirmationDialog';
import ResetAdminPasswordDialog from './ResetAdminPasswordDialog';
import AddSchoolAdminDialog from './AddSchoolAdminDialog';

// School logos are stored as bare filenames; route them through `/uploads/logos/`.
const resolveSchoolLogo = (logo?: string | null): string | undefined => {
  if (!logo) return undefined;
  if (/^https?:\/\//i.test(logo)) return logo;
  if (logo.startsWith('/')) return toUploadUrl(logo);
  return toUploadUrl('logos', logo);
};

interface Props {
  open: boolean;
  schoolId: string | null;
  onClose: () => void;
  onChanged: () => void; // Re-run the schools list query after any save.
}

const DRAWER_WIDTH_PX = 640;
const DRAWER_PAPER_SX = {
  width: { xs: '100vw', md: `${DRAWER_WIDTH_PX}px` },
  minWidth: { xs: '100vw', md: `${DRAWER_WIDTH_PX}px` },
  maxWidth: { xs: '100vw', md: `${DRAWER_WIDTH_PX}px` },
  flexShrink: 0,
  overflowX: 'hidden' as const,
  boxSizing: 'border-box' as const,
  bgcolor: 'background.default',
  backgroundImage: 'none'
};

interface SchoolDetailPayload extends School {
  stats?: { userCount: number; adminCount: number };
  subscriptionInfo?: {
    status: string;
    planName?: string;
    endDate?: string;
    billingCycle?: string;
  } | null;
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';

// Days until a subscription's end date — returns null if the subscription is
// pending payment (we render "—" in that case).
const remainingDays = (endDate?: string): number | null => {
  if (!endDate) return null;
  const ms = new Date(endDate).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
};

// Map subscription status → MUI chip color.
const subscriptionChipColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active': return 'success';
    case 'trial': return 'success';
    case 'pending_payment': return 'warning';
    case 'cancelled': return 'error';
    case 'expired': return 'error';
    default: return 'default';
  }
};

const SchoolDrawer: React.FC<Props> = ({ open, schoolId, onClose, onChanged }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [school, setSchool] = useState<SchoolDetailPayload | null>(null);
  const [admins, setAdmins] = useState<SchoolAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  // School edit state
  const [editingSchool, setEditingSchool] = useState(false);
  const [schoolDraft, setSchoolDraft] = useState({ name: '', email: '', phone: '', address: '' });
  const [savingSchool, setSavingSchool] = useState(false);

  // Per-admin edit state (only one row in-edit at a time)
  const [editingAdminId, setEditingAdminId] = useState<string | null>(null);
  const [adminDraft, setAdminDraft] = useState({ name: '', phone: '' });
  const [savingAdmin, setSavingAdmin] = useState(false);

  // Reset / add dialogs
  const [resetTarget, setResetTarget] = useState<SchoolAdmin | null>(null);
  const [addAdminOpen, setAddAdminOpen] = useState(false);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const [s, adminList] = await Promise.all([
        schoolService.getSchool(schoolId),
        schoolService.listAdmins(schoolId)
      ]);
      setSchool(s as SchoolDetailPayload);
      setAdmins(adminList);
      setSchoolDraft({
        name: (s as any).name || '',
        email: (s as any).email || '',
        phone: (s as any).phone || '',
        address: (s as any).address || ''
      });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load school', { variant: 'error' });
    } finally { setLoading(false); }
  }, [schoolId, enqueueSnackbar]);

  useEffect(() => {
    if (open && schoolId) {
      setTab(0);
      setEditingSchool(false);
      setEditingAdminId(null);
      load();
    }
    if (!open) {
      setSchool(null); setAdmins([]);
    }
  }, [open, schoolId, load]);

  const handleSaveSchool = async () => {
    if (!school) return;
    if (!schoolDraft.name.trim()) return enqueueSnackbar('Name is required', { variant: 'warning' });
    if (!schoolDraft.email.trim()) return enqueueSnackbar('Email is required', { variant: 'warning' });
    setSavingSchool(true);
    try {
      const updated = await schoolService.updateSchool(school._id, {
        name: schoolDraft.name.trim(),
        email: schoolDraft.email.trim(),
        phone: schoolDraft.phone.trim(),
        address: schoolDraft.address.trim()
      });
      setSchool(prev => prev ? { ...prev, ...(updated as any) } : prev);
      enqueueSnackbar('School updated', { variant: 'success' });
      setEditingSchool(false);
      onChanged();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Save failed', { variant: 'error' });
    } finally { setSavingSchool(false); }
  };

  const startEditAdmin = (admin: SchoolAdmin) => {
    setEditingAdminId(admin._id);
    setAdminDraft({ name: admin.name, phone: admin.phone || '' });
  };

  const handleSaveAdmin = async () => {
    if (!school || !editingAdminId) return;
    if (!adminDraft.name.trim()) return enqueueSnackbar('Name is required', { variant: 'warning' });
    setSavingAdmin(true);
    try {
      const updated = await schoolService.updateAdmin(school._id, editingAdminId, {
        name: adminDraft.name.trim(),
        phone: adminDraft.phone.trim()
      });
      setAdmins(prev => prev.map(a => a._id === editingAdminId ? { ...a, ...updated } : a));
      setEditingAdminId(null);
      enqueueSnackbar('Admin updated', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Save failed', { variant: 'error' });
    } finally { setSavingAdmin(false); }
  };

  const subscriptionInfo = school?.subscriptionInfo;
  const subDays = useMemo(() => remainingDays(subscriptionInfo?.endDate), [subscriptionInfo?.endDate]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      slotProps={{
        root: { style: { zIndex: 1250 } },
        paper: { style: { zIndex: 1250 }, sx: DRAWER_PAPER_SX }
      }}
    >
      {/* Header */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ px: 3.5, pt: 2.5, pb: 2 }}>
          <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Avatar
              src={resolveSchoolLogo(school?.logo)}
              variant="rounded"
              sx={{
                width: 52, height: 52,
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1),
                color: theme.palette.primary.main,
                fontSize: '1.1rem', fontWeight: 700,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.18)}`
              }}
            >
              {school ? initials(school.name) : <Business />}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                <Typography variant="h6" fontWeight={700} noWrap sx={{ fontSize: '1.1rem', lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                  {loading || !school ? <Skeleton width={180} /> : school.name}
                </Typography>
                {school && (
                  <Chip
                    size="small" label={school.isActive ? 'Active' : 'Inactive'}
                    color={school.isActive ? 'success' : 'default'} variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, color: 'text.secondary', flexWrap: 'wrap' }}>
                {!school ? <Skeleton width={220} /> : (
                  <>
                    <Box component="span" sx={{ fontFamily: 'monospace', letterSpacing: 0.3, fontWeight: 600, fontSize: 12, color: 'text.primary' }}>{school.code}</Box>
                    <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ fontSize: 12 }}>{school.email}</Typography>
                  </>
                )}
              </Stack>
            </Box>
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {school && (
          <Box sx={{ px: 3.5, py: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 3 }}>
            <StatCell label="Users" value={school.stats?.userCount ?? (school as any).userCount ?? 0} />
            <Divider orientation="vertical" flexItem />
            <StatCell label="Students" value={(school as any).studentCount ?? 0} />
            <Divider orientation="vertical" flexItem />
            <StatCell
              label="Subscription"
              value={subscriptionInfo?.planName || '—'}
              subline={
                subscriptionInfo
                  ? subscriptionInfo.status === 'pending_payment'
                    ? <Chip size="small" label="Pending payment" color={subscriptionChipColor(subscriptionInfo.status)} variant="outlined" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
                    : subDays != null ? `${subDays} day${subDays === 1 ? '' : 's'} left` : ''
                  : ''
              }
            />
          </Box>
        )}

        <Box sx={{ px: 3.5, borderTop: 1, borderColor: 'divider' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 42,
              '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 600, fontSize: 13, px: 0, mr: 3, minWidth: 0 },
              '& .MuiTabs-indicator': { height: 2, borderRadius: 2 }
            }}
          >
            <Tab label="Details" />
            <Tab label={`Admins${admins.length ? ` · ${admins.length}` : ''}`} />
            <Tab label="Subscription" />
          </Tabs>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3.5, py: 3, bgcolor: 'background.default' }}>
        {loading || !school ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={140} />
            <Skeleton variant="rounded" height={220} />
          </Stack>
        ) : (
          <>
            {tab === 0 && (
              <SectionCard
                title="School Details"
                action={
                  editingSchool ? (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<Cancel />} onClick={() => { setEditingSchool(false); setSchoolDraft({ name: school.name, email: school.email, phone: school.phone || '', address: school.address || '' }); }} disabled={savingSchool}>Cancel</Button>
                      <Button size="small" variant="contained" startIcon={savingSchool ? <CircularProgress size={14} color="inherit" /> : <Save />} onClick={handleSaveSchool} disabled={savingSchool}>{savingSchool ? 'Saving…' : 'Save'}</Button>
                    </Stack>
                  ) : (
                    <Button size="small" startIcon={<Edit />} onClick={() => setEditingSchool(true)}>Edit</Button>
                  )
                }
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField label="Name" size="small" required value={schoolDraft.name} disabled={!editingSchool} onChange={(e) => setSchoolDraft(d => ({ ...d, name: e.target.value }))} />
                  <TextField label="Code" size="small" value={school.code} disabled helperText="Immutable" />
                  <TextField label="Email" size="small" required value={schoolDraft.email} disabled={!editingSchool} onChange={(e) => setSchoolDraft(d => ({ ...d, email: e.target.value }))} />
                  <TextField label="Phone" size="small" value={schoolDraft.phone} disabled={!editingSchool} onChange={(e) => setSchoolDraft(d => ({ ...d, phone: e.target.value }))} />
                  <TextField label="Address" size="small" multiline minRows={1} value={schoolDraft.address} disabled={!editingSchool} onChange={(e) => setSchoolDraft(d => ({ ...d, address: e.target.value }))} sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }} />
                </Box>
              </SectionCard>
            )}

            {tab === 1 && (
              <SectionCard
                title="School Admins"
                action={
                  <Button size="small" variant="outlined" startIcon={<PersonAdd />} onClick={() => setAddAdminOpen(true)} sx={{ borderRadius: 1.5 }}>
                    Add Admin
                  </Button>
                }
              >
                {admins.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <Typography variant="body2">No admins yet. Add one so the school can sign in.</Typography>
                  </Box>
                ) : (
                  <Stack spacing={1.25}>
                    {admins.map(a => {
                      const isEditing = editingAdminId === a._id;
                      return (
                        <Box key={a._id} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                          {isEditing ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                              <TextField size="small" label="Name" required value={adminDraft.name} onChange={(e) => setAdminDraft(d => ({ ...d, name: e.target.value }))} />
                              <TextField size="small" label="Phone" value={adminDraft.phone} onChange={(e) => setAdminDraft(d => ({ ...d, phone: e.target.value }))} />
                              <TextField size="small" label="Email" value={a.email} disabled helperText="Email is the login identity and cannot be changed." sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }} />
                              <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' }, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button size="small" startIcon={<Cancel />} onClick={() => setEditingAdminId(null)} disabled={savingAdmin}>Cancel</Button>
                                <Button size="small" variant="contained" startIcon={savingAdmin ? <CircularProgress size={14} color="inherit" /> : <Save />} onClick={handleSaveAdmin} disabled={savingAdmin} sx={{ borderRadius: 1.5 }}>{savingAdmin ? 'Saving…' : 'Save'}</Button>
                              </Box>
                            </Box>
                          ) : (
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main', width: 40, height: 40, fontSize: 14, fontWeight: 700 }}>
                                {initials(a.name)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{a.name}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{a.email}</Typography>
                                {a.phone && <Typography variant="caption" color="text.secondary">{a.phone}</Typography>}
                              </Box>
                              <Stack direction="row" spacing={0.5}>
                                <Tooltip title="Edit admin">
                                  <IconButton size="small" onClick={() => startEditAdmin(a)}><Edit fontSize="small" /></IconButton>
                                </Tooltip>
                                <Tooltip title="Reset password">
                                  <IconButton size="small" onClick={() => setResetTarget(a)}><VpnKey fontSize="small" /></IconButton>
                                </Tooltip>
                              </Stack>
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </SectionCard>
            )}

            {tab === 2 && (
              <SectionCard title="Subscription" action={null}>
                {!subscriptionInfo ? (
                  <Typography variant="body2" color="text.secondary">No active subscription. Manage subscriptions from the Subscriptions page.</Typography>
                ) : (
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ width: 44, height: 44, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                        <EventAvailable fontSize="small" />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1" fontWeight={700}>{subscriptionInfo.planName || '—'}</Typography>
                          <Chip
                            size="small"
                            label={subscriptionInfo.status.replace('_', ' ')}
                            color={subscriptionChipColor(subscriptionInfo.status)}
                            variant="outlined"
                            sx={{ height: 20, fontSize: 10, fontWeight: 600, textTransform: 'capitalize' }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {subscriptionInfo.billingCycle || ''}
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      <ReadOnlyRow label="Plan" value={subscriptionInfo.planName || '—'} />
                      <ReadOnlyRow label="Billing cycle" value={subscriptionInfo.billingCycle || '—'} />
                      <ReadOnlyRow
                        label="Ends on"
                        value={subscriptionInfo.endDate && subscriptionInfo.status !== 'pending_payment'
                          ? new Date(subscriptionInfo.endDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                          : '—'}
                      />
                      <ReadOnlyRow
                        label="Remaining"
                        value={
                          subscriptionInfo.status === 'pending_payment'
                            ? 'Pending payment'
                            : subDays != null ? `${subDays} day${subDays === 1 ? '' : 's'}` : '—'
                        }
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      View only — manage plan changes from the Manage Subscriptions page.
                    </Typography>
                  </Stack>
                )}
              </SectionCard>
            )}
          </>
        )}
      </Box>

      {/* Dialogs */}
      {resetTarget && school && (
        <ResetAdminPasswordDialog
          open={!!resetTarget}
          admin={resetTarget}
          schoolId={school._id}
          onClose={() => setResetTarget(null)}
          onResetDone={() => setResetTarget(null)}
        />
      )}
      {school && (
        <AddSchoolAdminDialog
          open={addAdminOpen}
          schoolId={school._id}
          onClose={() => setAddAdminOpen(false)}
          onCreated={() => { setAddAdminOpen(false); load(); onChanged(); }}
        />
      )}
    </Drawer>
  );
};

// ===== Local helpers =====

const StatCell: React.FC<{ label: string; value: React.ReactNode; subline?: React.ReactNode }> = ({ label, value, subline }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: 10 }}>
      {label}
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
    {subline && <Typography variant="caption" color="text.secondary" component="div">{subline}</Typography>}
  </Box>
);

const SectionCard: React.FC<{ title: string; action: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 2.25 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>{title}</Typography>
      {action}
    </Stack>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

const ReadOnlyRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: 10 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ mt: 0.25 }}>{value}</Typography>
  </Box>
);

export default SchoolDrawer;
